import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface OrderItem {
  product_id: string;
  product_name: string;
  product_image: string;
  quantity: number;
  price: number;
  total: number;
}

interface CreateOrderData {
  customer_name: string;
  customer_phone: string;
  shipping_address: string;
  shipping_city: string;
  shipping_notes?: string;
  payment_method: 'cash' | 'vodafone' | 'instapay';
  total_amount: number;
  shipping_fee: number;
  items: OrderItem[];
}

export function useOrders() {
  const [loading, setLoading] = useState(false);

  const createOrder = async (orderData: CreateOrderData) => {
    try {
      setLoading(true);

      console.log('🔍 Creating order:', orderData);

      // إنشاء الطلب
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert([{
          customer_name: orderData.customer_name,
          customer_phone: orderData.customer_phone,
          shipping_address: orderData.shipping_address,
          shipping_city: orderData.shipping_city,
          shipping_notes: orderData.shipping_notes || '',
          payment_method: orderData.payment_method,
          total_amount: orderData.total_amount,
          shipping_fee: orderData.shipping_fee,
          items: orderData.items,
          payment_status: 'pending',
          order_status: 'pending'
        }])
        .select()
        .single();

      if (orderError) {
        console.error('❌ Order creation error:', orderError);
        throw orderError;
      }

      console.log('✅ Order created:', order);

      // إنشاء عناصر الطلب
      const orderItems = orderData.items.map(item => ({
        order_id: order.id,
        product_id: item.product_id,
        product_name: item.product_name,
        product_image: item.product_image,
        quantity: item.quantity,
        price: item.price,
        total: item.total
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('❌ Order items error:', itemsError);
        throw itemsError;
      }

      console.log('✅ Order items created');

      return { success: true, order };
    } catch (error) {
      console.error('❌ Error creating order:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create order'
      };
    } finally {
      setLoading(false);
    }
  };

  const getOrders = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch orders'
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createOrder,
    getOrders
  };
}
